import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { TRAVEL_RECEIPTS_DATA } from '@work4all/components/lib/components/entity-preview/travel-receipts-preview/TravelReceiptsPreviewContainer';

import { useUser } from '@work4all/data';

import { TravelExpenses } from '@work4all/models/lib/Classes/TravelExpenses.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';
import { TravelExpensesKind } from '@work4all/models/lib/Enums/TravelExpensesKind.enum';

import { useSupplier } from '../../../../../files/detail/components/queries/use-supplier';
import { MaskExtendedConfig } from '../../../types';

export const useTravelExpenses = (
  mask: MaskExtendedConfig<string>,
  _defaults: TravelExpenses
) => {
  const request = useMemo<DataRequest>(() => {
    const filter = [{ id: { $eq: mask.id } }];
    const entity = Entities.travelExpenses;
    const data = {
      id: null,
      date: null,
      travelStart: null,
      travelEnd: null,
      workStart: null,
      workEnd: null,
      note: null,
      travelPurpose: null,
      city: null,
      country: {
        id: null,
        shortName: null,
        longtext: null,
      },
      travelExpensesKindId: null,
      businessTripContractNumber: null,
      projectId: null,
      costCenterId: null,
      costCenter: {
        id: null,
        name: null,
      },
      payoutAmount: null,
      advance: null,
      receiptSum: null,
      approved: null,
      closedByuserid: null,
      closeDate: null,
      user: {
        id: null,
        displayName: null,
      },
      project: {
        id: null,
        name: null,
        number: null,
      },
      customerId: null,
      businessPartnerId: null,
      businessPartnerType: null,
      businessPartner: {
        id: null,
        data: {
          customer: {
            id: null,
            name: null,
          },
          supplier: {
            id: null,
            name: null,
          },
        },
      },
      visitedbusinessPartnerSDObjMemberId: null,
      visitedbusinessPartnerSDObjType: null,
      visitedContact: {
        id: null,
        data: {
          customer: {
            id: null,
            name: null,
          },
          supplier: {
            id: null,
            name: null,
          },
        },
      },
      receipts: [
        {
          id: null,
          ...TRAVEL_RECEIPTS_DATA,
          paymentKind: null,
          expenditure: null,
          description: null,
          date: null,
          receiptKindTravelCost: {
            id: null,
            name: null,
            enumReceiptKindTravelCosts: null,
          },
          paymentMethod: {
            id: null,
            name: null,
          },
          travelExpensesId: null,
          receiptKind: {
            id: null,
            name: null,
            typeOfReceiptType: null,
          },
          travelExpenses: {
            id: null,
            user: {
              id: null,
              displayName: null,
            },
          },
          costCenterId: null,
          costCenter: {
            id: null,
            name: null,
            number: null,
          },
          vat: null,
          vat1: null,
          vat2: null,
          vat3: null,
          ePrice: null,
          amount: null,
          amountGross1: null,
          amountGross2: null,
          amountGross3: null,
          ledgerAccount1Id: null,
          ledgerAccount2Id: null,
          ledgerAccount3Id: null,
          ledgerAccount1: {
            id: null,
            name: null,
            number: null,
          },
          ledgerAccount2: {
            id: null,
            name: null,
            number: null,
          },
          ledgerAccount3: {
            id: null,
            name: null,
            number: null,
          },
          taxKey1: {
            id: null,
            taxKeyValue: null,
          },
          taxKey2: {
            id: null,
            taxKeyValue: null,
          },
          taxKey3: {
            id: null,
            taxKeyValue: null,
          },
        },
      ],
      additionalMealExpenses: [
        {
          id: null,
          travelExpensesId: null,
          dateTime: null,
          amount: null,
          hours: null,
          deductionBreakfast: null,
          deductionBreakfastAmount: null,
          deductionLunch: null,
          deductionLunchAmount: null,
          deductionDinner: null,
          deductionDinnerAmount: null,
          projectId: null,
          businessPartnerId: null,
          businessPartnerType: null,
          note: null,
          project: {
            id: null,
            name: null,
            number: null,
          },
          company: {
            id: null,
            data: {
              customer: {
                id: null,
                name: null,
              },
              supplier: {
                id: null,
                name: null,
              },
            },
          },
        },
      ],
    };
    return {
      filter,
      entity,
      data,
    };
  }, [mask.id]);

  const user = useUser();
  const { supplier } = useSupplier(user?.supplierCode?.toString());

  const newEntityData = useMemo<TravelExpenses>(() => {
    return {
      businessTripContractNumber: 0,
      date: DateTime.now()
        .endOf('month')
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toISO(),
      travelStart: DateTime.now()
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
        .toISO(),
      travelEnd: DateTime.now()
        .set({ hour: 16, minute: 0, second: 0, millisecond: 0 })
        .toISO(),
      travelExpensesKindId: TravelExpensesKind.STANDARD,
      payoutAmount: 0,
      advance: 0,
      receiptSum: 0,
      approved: 0,
      businessPartner: supplier,
      businessPartnerId: supplier?.id,
      businessPartnerType: SdObjType.LIEFERANT,
    };
  }, [supplier]);

  return { request, newEntityData };
};
